import { Col, Row, Typography } from "antd";
import { useEventContext } from "../../../context/EventContext/EventContext";
import { EventDetailLocalizableFormData, VolunteersEventType, EventTextRules } from "../../../context/EventContext/constants";
import InputComponent from "../../../components/InputComponent";
import RichTextComponent from "../../../components/RichTextComponent";
import FileUploadComponent, { FileData } from "../../../components/FileUploadComponent";
import { DetailImageUpload, DetailTextConstants, DetailTextLengthConstants } from "./constants";
import { CultureProps } from "../constants";

type RichTextKey = "detailText" | "processText" | "applicantText" | "durationText";

const EventDetailContent = ({ Culture }: CultureProps) => {
	const { eventEditForm: eventUpdateForm, setEventEditForm: setEventUpdateForm, eventDetail } = useEventContext();

	if (!eventUpdateForm) {
		return null;
	}

	const fields = DetailTextConstants[Culture];
	const isApplicationRequired = eventUpdateForm.eventType === VolunteersEventType.ApplicationRequired;

	const localized = eventUpdateForm.eventDetail.eventDetailLocalizable.find(x => x.culture === Culture);
	const defaultImageUrl = eventDetail?.eventDetail?.eventDetailLocalizable.find(x => x.culture === Culture)?.imageUrl;

	const handleChange = <K extends keyof EventDetailLocalizableFormData>(key: K, value: EventDetailLocalizableFormData[K]) => {
		setEventUpdateForm(prev => prev ? {
			...prev,
			eventDetail: {
				...prev.eventDetail,
				eventDetailLocalizable: prev.eventDetail.eventDetailLocalizable.map(item =>
					item.culture === Culture ? { ...item, [key]: value } : item
				),
			},
		} : null);
	};

	const richTextKeys: RichTextKey[] = isApplicationRequired
		? ["detailText", "processText", "applicantText", "durationText"]
		: ["detailText", "processText", "applicantText"];

	return (
		<>
			<Row gutter={[20, 16]}>
				<Col span={24}>
					<InputComponent
						title={fields.header.label}
						placeholder={fields.header.placeholder}
						isRequired
						value={localized?.header}
						maxLength={EventTextRules.detailHeaderMaxLength}
						onChange={e => handleChange("header", e.target.value)}
					/>
				</Col>
				<Col xs={24} lg={14}>
					<Row gutter={[0, 16]}>
						{richTextKeys.map(key => (
							<Col span={24} key={key}>
								<Typography.Text>
									{fields[key].label}
									{key === "detailText" && <span style={{ color: "red" }}> *</span>}
								</Typography.Text>
								<RichTextComponent
									height={DetailTextLengthConstants[key]}
									resizable={false}
									messageTemplate={localized?.[key]}
									backgroundColor={"transparent"}
									placeholder={fields[key].placeholder}
									handleSetChangedTemplate={body => handleChange(key, body)}
									maxLength={key === "durationText" ? EventTextRules.durationTextMaxLength : EventTextRules.detailTextMaxLength}
									lineBreaks={EventTextRules.lineBreaks}
								/>
							</Col>
						))}
					</Row>
				</Col>
				<Col xs={24} lg={10}>
					<FileUploadComponent
						title={fields.image.label}
						isRequired
						maxFileSize={DetailImageUpload.maxFileSize}
						allowedExtensions={DetailImageUpload.extensions}
						dimensions={DetailImageUpload.dimensions}
						defaultUrl={defaultImageUrl}
						onChange={(files: FileData[] | null) => handleChange("image", files?.[0]?.file ?? null)}
					/>
				</Col>
			</Row>
		</>
	);
};

export const EventCustomButtonContent = ({ Culture }: CultureProps) => {
	const { eventEditForm: eventUpdateForm, setEventEditForm: setEventUpdateForm } = useEventContext();

	if (!eventUpdateForm) {
		return null;
	}

	const labels = DetailTextConstants[Culture];
	const localized = eventUpdateForm.eventDetail.eventDetailLocalizable.find(x => x.culture === Culture);

	const handleChange = (key: "customButtonText" | "customButtonUrl", value: string) => {
		setEventUpdateForm(prev => prev ? {
			...prev,
			eventDetail: {
				...prev.eventDetail,
				eventDetailLocalizable: prev.eventDetail.eventDetailLocalizable.map(item =>
					item.culture === Culture ? { ...item, [key]: value } : item
				),
			},
		} : null);
	};

	return (
		<>
			<Row gutter={[20, 16]}>
				<Col xs={24} md={6}>
					<InputComponent
						title={labels.customButtonText.label}
						placeholder={labels.customButtonText.placeholder}
						isRequired
						value={localized?.customButtonText}
						maxLength={EventTextRules.customButtonTextMaxLength}
						onChange={e => handleChange("customButtonText", e.target.value)}
					/>
				</Col>
				<Col xs={24} md={18}>
					<InputComponent
						title={labels.customButtonUrl.label}
						placeholder={labels.customButtonUrl.placeholder}
						isRequired
						value={localized?.customButtonUrl}
						maxLength={EventTextRules.customButtonUrlMaxLength}
						onChange={e => handleChange("customButtonUrl", e.target.value)}
					/>
				</Col>
			</Row>
		</>
	);
};

export default EventDetailContent;
